/**
 * Log levels following standard severity hierarchy.
 * Used to determine which logs should be sent based on debug mode state.
 */
export enum LogLevel {
  Trace = 'trace',
  Debug = 'debug',
  Info = 'info',
  Warn = 'warn',
  Error = 'error',
  Fatal = 'fatal',
}

/**
 * Base interface for all log entries.
 */
export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp?: Date;
  attributes?: Record<string, unknown>;
}

/**
 * GraphQL operation types for logging.
 */
export type GraphQLOperationType = 'query' | 'mutation' | 'subscription';

/**
 * Extended log entry for GraphQL operations.
 * Captures full request/response details for debugging.
 */
export interface GraphQLLogEntry extends Omit<LogEntry, 'level' | 'message'> {
  operationName: string;
  operationType: GraphQLOperationType;
  variables?: Record<string, unknown>;
  response?: unknown;
  error?: Error;
  durationMs: number;
}

/**
 * Extended log entry for navigation/route changes.
 */
export interface NavigationLogEntry extends Omit<
  LogEntry,
  'level' | 'message'
> {
  pathname: string;
  search?: string;
  previousPathname?: string;
}

/**
 * Configuration options for the logger service.
 */
export interface LoggerConfig {
  isDebugModeEnabled: boolean;
  userId?: string | null;
  userEmail?: string | null;
}

/**
 * Context value provided by DebugModeProvider.
 */
export interface DebugModeContextValue {
  isDebugMode: boolean;
  debugModeUntil: string | null;
}

/**
 * Extended log entry for REST API calls.
 * Captures request/response details for debugging.
 */
export interface RestLogEntry {
  method: string;
  url: string;
  statusCode?: number;
  requestBody?: unknown;
  responseBody?: unknown;
  error?: Error;
  durationMs: number;
}
